// src/components/admin/NewsPostsTab.js
import React, { useState, useMemo } from 'react';
import apiClient from '../../utils/api'; // <-- IMPORTANT: Import your configured apiClient
import AdminNewsDetailModal from '../AdminNewsDetailModal';
import AiStatusCell from './AiStatusCell';
import '../../pages/AdminDashboard.css';

import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const NewsPostsTab = ({ newsData, setNewsData, onDataRefresh }) => {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNews, setSelectedNews] = useState(null);
  const [notifyState, setNotifyState] = useState({});
  const [loadingAction, setLoadingAction] = useState(null); 

  // --- NEW: EDIT STATES ---
  const [isEditMode, setIsEditMode] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
  };

  // --- NEW: OPEN MODAL PRE-FILLED WITH VALUES ---
  const handleOpenDetails = (item) => {
    setSelectedNews(item);
    setEditTitle(item.title || '');
    setEditContent(item.content || item.description || '');
    setIsEditMode(false);
  };

  // --- NEW: SUBMIT EDITED POST TO SERVER ---
  const handleSaveEdit = async () => {
    if (!editTitle.trim() || !editContent.trim()) {
        alert("Title and Content cannot be empty.");
        return;
    }
    setIsSaving(true);
    try {
        await apiClient.put(`/admin/user-news/${selectedNews.id}`, {
            title: editTitle,
            content: editContent,
            description: editContent // Update both fields to remain compatible with older database schemas
        });
        alert("News post updated successfully!");
        setIsEditMode(false);
        setSelectedNews(null); // Close the modal
        onDataRefresh(); // Refresh the table list in dashboard
    } catch (error) {
        console.error("Error updating news post:", error);
        alert(`Failed to update post: ${error.response?.data?.message || error.message}`);
    } finally {
        setIsSaving(false);
    }
  };

  const handleAction = async (item, actionType) => { 
        let confirmMessage = '';
        let apiCall = null;

        setLoadingAction(`${item.id}-${actionType}`); 

        try {
            switch (actionType) {
                case 'approve':
                    confirmMessage = 'Are you sure you want to MANUALLY APPROVE this post?';
                    apiCall = () => apiClient.put( 
                        `/admin/user-news/${item.id}/status`,
                        { status: 'approved', notify: !!notifyState[item.id] }
                    );
                    break;
                case 'reject':
                    confirmMessage = 'Are you sure you want to MANUALLY REJECT this post?';
                    apiCall = () => apiClient.put( 
                        `/admin/user-news/${item.id}/status`,
                        { status: 'rejected' }
                    );
                    break;
                case 'toggleTopNews':
                    confirmMessage = `Are you sure you want to ${item.isTopNews ? 'remove from' : 'make this a'} Top News?`;
                    apiCall = () => apiClient.put( 
                        `/admin/user-news/${item.id}/status`,
                        { isTopNews: !item.isTopNews }
                    );
                    break;
                case 'delete':
                    confirmMessage = 'Are you sure you want to PERMANENTLY DELETE this post?';
                    apiCall = () => apiClient.delete( 
                        `/admin/news/${item.id}`
                    );
                    break;
                default:
                    setLoadingAction(null); 
                    return;
            }

            if (window.confirm(confirmMessage)) {
                await apiCall();
                onDataRefresh(); 
                alert(`Post ${actionType}d successfully!`);
            }
        } catch (error) {
            console.error(`Error performing action ${actionType} for item ${item.id}:`, error.response?.data || error.message);
            alert(`Failed to ${actionType} post: ${error.response?.data?.message || error.message}`);
        } finally {
            setLoadingAction(null); 
        }
    };

    const filteredNews = useMemo(() => {
        return newsData.filter(item => {
            let matchesStatus = false;
            if (filter === 'all') matchesStatus = true;
            else if (filter === 'rejected') matchesStatus = item.status?.startsWith('rejected');
            else if (filter === 'pending') matchesStatus = item.status?.startsWith('pending');
            else matchesStatus = item.status === filter;

            const s = searchTerm.toLowerCase();
            const matchesSearch = 
                item.title?.toLowerCase().includes(s) || 
                item.description?.toLowerCase().includes(s) || 
                item.userName?.toLowerCase().includes(s);

            return matchesStatus && matchesSearch;
        });
    }, [filter, searchTerm, newsData]);

  return (
        <div>
            <div className="admin-controls-row" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', alignItems: 'center' }}>
                <div className="filter-buttons" style={{ margin: 0 }}>
                    <button onClick={() => setFilter('all')} className={filter === 'all' ? 'active' : ''}>All ({newsData.length})</button>
                    <button onClick={() => setFilter('pending')} className={filter === 'pending' ? 'active' : ''}>Pending ({newsData.filter(n => n.status && n.status.startsWith('pending')).length})</button>
                    <button onClick={() => setFilter('approved')} className={filter === 'approved' ? 'active' : ''}>Approved ({newsData.filter(n => n.status === 'approved').length})</button>
                    <button onClick={() => setFilter('rejected')} className={filter === 'rejected' ? 'active' : ''}>Rejected ({newsData.filter(n => n.status && n.status.startsWith('rejected')).length})</button>
                </div>
                <div className="search-container">
                    <input 
                        type="text" 
                        placeholder="Search News Title, Author or Content..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="admin-search-input"
                        style={{ width: '350px' }}
                    />
                </div>
            </div>
            <div className="table-wrapper">
                <table className="news-table">
                    <thead>
                        <tr>
                            <th>Preview</th>
                            <th>Title</th>
                            <th>Author</th>
                            <th>Location</th>
                            <th>Current Status</th>
                            <th>AI Moderation</th>
                            <th>Actions</th>
                            <th>Details</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredNews.length === 0 ? (
                            <tr>
                                <td colSpan="8" style={{ textAlign: 'center', padding: '20px' }}>No news posts found for this filter.</td>
                            </tr>
                        ) : (
                            filteredNews.map(item => (
                                <tr key={item.id}>
                                    <td className="admin-preview-cell">
                                        {item.videoUrl ? (
                                            <div className="admin-table-video-wrapper">
                                                <video src={item.videoUrl} className="admin-table-preview" muted preload="metadata" />
                                                <span className="video-icon-overlay">▶</span>
                                            </div>
                                        ) : item.imageUrls && item.imageUrls.length > 0 ? (
                                            <img src={item.imageUrls[0]} alt="preview" className="admin-table-preview" />
                                        ) : (
                                            <div className="no-preview">No Media</div>
                                        )}
                                    </td>
                                    <td>{item.title}</td>
                                    <td>{item.userName}<br />{item.userContact}</td>
                                    <td>{item.postType === 'India' ? 'India' : `${item.state || ''}${item.district ? `, ${item.district}` : ''}`}</td>
                                    <td><span className={`status-pill status-${item.status}`}>{item.status}</span></td>

                                    <td><AiStatusCell post={item} /></td>

                                    <td className="action-buttons">
                                        {item.status !== 'approved' && (
                                            <button
                                                className="approve-btn"
                                                onClick={() => handleAction(item, 'approve')}
                                                disabled={loadingAction === `${item.id}-approve`}
                                            >
                                                {loadingAction === `${item.id}-approve` ? 'Approving...' : 'Approve'}
                                            </button>
                                        )}
                                        {item.status === 'approved' && (
                                            <button
                                                className="toggle-top-news-btn"
                                                onClick={() => handleAction(item, 'toggleTopNews')}
                                                disabled={loadingAction === `${item.id}-toggleTopNews`}
                                            >
                                                {loadingAction === `${item.id}-toggleTopNews` ? 'Updating...' : (item.isTopNews ? "Remove Top" : "Make Top")}
                                            </button>
                                        )}
                                        {!item.status.startsWith('rejected') && (
                                            <button
                                                className="reject-btn"
                                                onClick={() => handleAction(item, 'reject')}
                                                disabled={loadingAction === `${item.id}-reject`}
                                            >
                                                {loadingAction === `${item.id}-reject` ? 'Rejecting...' : 'Reject'}
                                            </button>
                                        )}
                                        <button
                                            className="delete-btn"
                                            onClick={() => handleAction(item, 'delete')}
                                            disabled={loadingAction === `${item.id}-delete`}
                                        >
                                            {loadingAction === `${item.id}-delete` ? 'Deleting...' : 'Delete'}
                                        </button>
                                    </td>
                                    {/* --- Updated to use handleOpenDetails --- */}
                                    <td className="details-cell">
                                        <button className="details-btn" onClick={() => handleOpenDetails(item)}>
                                            <i className="fas fa-ellipsis-v"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {selectedNews && (
        <div className="modal-overlay" onClick={() => setSelectedNews(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', width: '90%' }}>
            <button className="modal-close-button" onClick={() => setSelectedNews(null)}>&times;</button>
            
            <h2 style={{ marginBottom: '15px' }}>
                {isEditMode ? "Edit News Post" : "News Post Details"}
            </h2>

            {/* --- MEDIA SECTION: VIDEO OR IMAGES --- */}
            <div className="local-detail-media" style={{ marginBottom: '20px', background: '#000', borderRadius: '8px', overflow: 'hidden' }}>
                {selectedNews.videoUrl ? (
                    <video 
                        src={selectedNews.videoUrl} 
                        controls 
                        autoPlay
                        style={{ width: '100%', maxHeight: '450px', display: 'block' }} 
                    />
                ) : selectedNews.imageUrls && selectedNews.imageUrls.length > 0 ? (
                    selectedNews.imageUrls.length > 1 ? (
                        <Slider {...sliderSettings}>
                            {selectedNews.imageUrls.map((url, idx) => (
                                <div key={idx}><img src={url} alt="detail" style={{ width: '100%', maxHeight: '450px', objectFit: 'contain' }} /></div>
                            ))}
                        </Slider>
                    ) : (
                        <img src={selectedNews.imageUrls[0]} alt="detail" style={{ width: '100%', maxHeight: '450px', objectFit: 'contain' }} />
                    )
                ) : (
                    <div style={{ padding: '40px', color: '#fff', textAlign: 'center' }}>No Images or Video</div>
                )}
            </div>

            {/* --- DETAILED / EDITABLE CONTENT --- */}
            <div className="local-detail-body" style={{ textAlign: 'left' }}>
                {isEditMode ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        <div>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Title:</label>
                            <input 
                                type="text"
                                value={editTitle}
                                onChange={(e) => setEditTitle(e.target.value)}
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                            />
                        </div>
                        <div>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Content/Description:</label>
                            <textarea 
                                rows="6"
                                value={editContent}
                                onChange={(e) => setEditContent(e.target.value)}
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', resize: 'vertical' }}
                            />
                        </div>
                    </div>
                ) : (
                    <>
                        <h3 style={{ color: '#333' }}>{selectedNews.title}</h3>
                        <p style={{ whiteSpace: 'pre-wrap', margin: '15px 0', color: '#555' }}>
                            {selectedNews.content || selectedNews.description}
                        </p>
                    </>
                )}
                
                <hr style={{ margin: '15px 0' }} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px', marginTop: '10px' }}>
                    <p><strong>Author:</strong> {selectedNews.userName}</p>
                    <p><strong>Contact:</strong> {selectedNews.userContact}</p>
                    <p><strong>Media:</strong> {selectedNews.videoUrl ? 'Video' : 'Images'}</p>
                    <p><strong>Status:</strong> {selectedNews.status}</p>
                </div>
            </div>

            {/* --- FOOTER BUTTONS --- */}
            <div style={{ marginTop: '20px', textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                {isEditMode ? (
                    <>
                        <button 
                            className="details-btn" 
                            onClick={handleSaveEdit} 
                            disabled={isSaving}
                            style={{ background: '#4CAF50', color: '#fff' }}
                        >
                            {isSaving ? "Saving..." : "Save Changes"}
                        </button>
                        <button 
                            className="details-btn" 
                            onClick={() => setIsEditMode(false)} 
                            disabled={isSaving}
                            style={{ background: '#eee', color: '#333' }}
                        >
                            Cancel
                        </button>
                    </>
                ) : (
                    <>
                        <button 
                            className="details-btn" 
                            onClick={() => setIsEditMode(true)} 
                            style={{ background: '#2196F3', color: '#fff' }}
                        >
                            Edit Post
                        </button>
                        <button 
                            className="details-btn" 
                            onClick={() => setSelectedNews(null)} 
                            style={{ background: '#eee', color: '#333' }}
                        >
                            Close
                        </button>
                    </>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsPostsTab;